import { useState } from "react";
import { useWindowed, ShowMoreBar } from "@/components/ShowMore";
import { UserRound, LogIn, LogOut, NotebookPen, Clock } from "lucide-react";
import { ClientStayDialog, StayNotesDialog, fmtT, fmtDur } from "./ClientStayDialog";

export function ClientGrid({ clients, pin = "", onChanged, big = false, extraActions, onOpen, onStayDone }) {
  const [dlg, setDlg] = useState(null); // {client, mode} | {client, notes:true}
  const clientWin = useWindowed(clients);
  return (
    <>
      <div className={`grid gap-3 ${big ? "grid-cols-2 md:grid-cols-3 xl:grid-cols-4" : "grid-cols-1 md:grid-cols-2 xl:grid-cols-3"}`} data-testid="client-grid">
        {clientWin.items.map((c) => {
          const st = c.current_stay;
          return (
            <div key={c.id} data-testid={`client-card-${c.id}`}
              className={`rounded-2xl border bg-white p-4 flex flex-col gap-3 transition ${c.on_site ? "border-emerald-300 shadow-[0_0_0_3px_rgba(16,185,129,0.12)]" : c.expected_today ? "border-amber-200" : "border-slate-200"}`}>
              <div className="flex items-center gap-3 min-w-0">
                <div className="h-14 w-14 shrink-0 rounded-xl bg-slate-100 overflow-hidden flex items-center justify-center text-slate-400">
                  {c.photo_data_url ? <img src={c.photo_data_url} alt="" className="h-full w-full object-cover" /> : <UserRound className="h-7 w-7" />}
                </div>
                <div className="min-w-0 flex-1">
                  {onOpen ? (
                    <button onClick={() => onOpen(c)} data-testid={`client-name-${c.id}`} className="font-display font-bold text-slate-900 truncate hover:text-primary hover:underline text-left block max-w-full">{c.full_name}</button>
                  ) : (
                    <div className="font-display font-bold text-slate-900 truncate" data-testid={`client-name-${c.id}`}>{c.full_name}</div>
                  )}
                  {c.on_site ? (
                    <div className="text-xs text-emerald-700 font-semibold inline-flex items-center gap-1" data-testid={`client-status-${c.id}`}>
                      <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" /> On site since {fmtT(st.arrived_at)} · {fmtDur(st.minutes_so_far)}
                    </div>
                  ) : (
                    <div className={`text-xs ${c.expected_today ? "text-amber-700 font-semibold" : "text-slate-400"}`} data-testid={`client-status-${c.id}`}>
                      {c.expected_today ? "Expected today · not arrived" : "Not on site"}
                    </div>
                  )}
                  {c.needs && <div className="text-[11px] text-slate-500 truncate" title={c.needs}>{c.needs}</div>}
                </div>
              </div>
              <div className="flex items-center gap-1.5">
                {c.on_site ? (
                  <>
                    <button onClick={() => setDlg({ client: c, mode: "depart" })} data-testid={`client-depart-${c.id}`}
                      className={`flex-1 inline-flex items-center justify-center gap-1.5 rounded-lg bg-blue-600 text-white font-semibold hover:bg-blue-700 transition ${big ? "h-12 text-sm" : "h-9 text-xs"}`}>
                      <LogOut className="h-4 w-4" /> Check out
                    </button>
                    <button onClick={() => setDlg({ client: c, notes: true })} data-testid={`client-notes-${c.id}`} title="Stay notes"
                      className={`inline-flex items-center justify-center gap-1 rounded-lg border border-slate-200 bg-white text-slate-700 hover:bg-slate-100 px-3 ${big ? "h-12 text-sm" : "h-9 text-xs"}`}>
                      <NotebookPen className="h-4 w-4" /> {st.entries?.length || 0}
                    </button>
                  </>
                ) : (
                  <button onClick={() => setDlg({ client: c, mode: "arrive" })} data-testid={`client-arrive-${c.id}`}
                    className={`flex-1 inline-flex items-center justify-center gap-1.5 rounded-lg bg-emerald-600 text-white font-semibold hover:bg-emerald-700 transition ${big ? "h-12 text-sm" : "h-9 text-xs"}`}>
                    <LogIn className="h-4 w-4" /> Check in
                  </button>
                )}
                {extraActions?.(c)}
              </div>
            </div>
          );
        })}
        {clients.length === 0 && <div className="col-span-full rounded-2xl border border-dashed border-slate-300 p-10 text-center text-sm text-slate-400 inline-flex items-center justify-center gap-2"><Clock className="h-4 w-4" /> No clients yet.</div>}
      </div>
      <ShowMoreBar win={clientWin} testid="clients-show-more" />
      {dlg?.mode && <ClientStayDialog client={dlg.client} mode={dlg.mode} pin={pin} onClose={() => setDlg(null)} onDone={() => { onChanged?.(); onStayDone?.(); }} />}
      {dlg?.notes && <StayNotesDialog client={dlg.client} stay={dlg.client.current_stay} pin={pin} onClose={() => setDlg(null)} onDone={onChanged} />}
    </>
  );
}
